import Layout from '@/components/Layout';
import { buildContentArticlesPageProps } from '@/services/page-content';
import { parseEditorjsHtml } from '@/services/directus';
import PageHeroIntro from '@/components/PageHeroIntro';
import { ReactElement } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import { getImageUrl } from '@/utils/utils';
import {
  aboutBodySectionSx,
  aboutCardSx,
  highlightsInnerSx,
  highlightsListSx,
  highlightsPaneSx,
  highlightsTitleSx,
  offerCardSx,
  offerDescriptionSx,
  offerGridSx,
  offerImageWrapSx,
  offerSectionSx,
  offerSectionTitleSx,
  offerTitleSx,
  whoWeAreContentSx,
  whoWeArePaneSx,
  whoWeAreTitleSx,
} from '@/styles/about-page-style';

type AboutPageProps = {
  content: any;
  whoWeAreHtml: string;
};

const HEADER_TRANSPARENT_COLOR = 'rgba(1, 47, 91, 0.01)';

type HighlightItem = {
  line?: string;
};

type OfferItem = {
  id?: number | string;
  headline?: string;
  description?: string;
  image?: string;
};

const AboutPage = ({ content, whoWeAreHtml }: AboutPageProps) => {
  const highlights = Array.isArray(content?.highlights) ? content.highlights : [];
  const offers = Array.isArray(content?.offers)
    ? content.offers.filter(
        (offer: unknown): offer is OfferItem =>
          Boolean(offer) && typeof offer === 'object' && !Array.isArray(offer),
      )
    : [];
  const heroImageSrc = content?.hero_thumbnail
    ? getImageUrl(content.hero_thumbnail)
    : '/assets/images/2369425270.jpeg';

  return (
    <>
      <PageHeroIntro
        title={content?.hero_title}
        description={content?.hero_description}
        heroImageSrc={heroImageSrc}
        heroImageAlt={content?.hero_title ?? 'HoiAnGO About'}
      />

      <Box component="section" sx={aboutBodySectionSx}>
        <Container maxWidth="lg" sx={{ paddingBottom: { xs: 3, md: 4 , lg: 8 } }}>
          <Box sx={aboutCardSx}>
            <Box sx={whoWeArePaneSx}>
              <Typography component="h2" sx={whoWeAreTitleSx}>
                Who We Are
              </Typography>
              {whoWeAreHtml ? (
                <Box sx={whoWeAreContentSx} dangerouslySetInnerHTML={{ __html: whoWeAreHtml }} />
              ) : null}
            </Box>

            {highlights.length > 0 ? (
              <Box sx={{ ...highlightsPaneSx}}>
                <Box sx={{ ...highlightsInnerSx}}>
                  {content?.highlights_title ? (
                    <Typography component="h2" sx={highlightsTitleSx}>
                      {content.highlights_title}
                    </Typography>
                  ) : null}
                  <Box component="ul" sx={{ ...highlightsListSx }}>
                    {highlights.map((item: HighlightItem, index: number) => (
                      <li key={`${item?.line ?? 'highlight'}-${index}`}>{item?.line ?? ''}</li>
                    ))}
                  </Box>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Container>

        {offers.length > 0 ? (
          <Box component="section" sx={offerSectionSx}>
            <Container maxWidth="lg">
              <Typography component="h2" sx={offerSectionTitleSx}>
                What We Offer
              </Typography>
              <Box sx={offerGridSx}>
                {offers.map((offer: OfferItem, index: number) => {
                  const offerImageSrc = offer?.image
                    ? getImageUrl(offer.image)
                    : '/assets/images/logo.png';
                  const offerTitle = offer?.headline?.trim() || '';
                  const offerDescription = offer?.description?.trim() || '';

                  return (
                    <Box key={`${offer?.id ?? offerTitle}-${index}`} sx={offerCardSx}>
                      <Box sx={offerImageWrapSx}>
                        <Image
                          src={offerImageSrc}
                          alt={offerTitle || 'HoiAnGO offer'}
                          fill
                          sizes="(max-width: 900px) 100vw, 50vw"
                          style={{ objectFit: 'cover' }}
                        />
                      </Box>
                      {offerTitle ? (
                        <Typography component="h3" sx={offerTitleSx}>
                          {offerTitle}
                        </Typography>
                      ) : null}
                      {offerDescription ? (
                        <Typography component="p" sx={offerDescriptionSx}>
                          {offerDescription}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            </Container>
          </Box>
        ) : null}
      </Box>
    </>
  );
};

AboutPage.getLayout = (page: ReactElement) => (
  <Layout showTabs hasFooter headerBackgroundColor={HEADER_TRANSPARENT_COLOR}>
    {page}
  </Layout>
);

AboutPage.type = 'public';

export async function getServerSideProps({ locale }: { locale: string }) {
  const pageProps = await buildContentArticlesPageProps({
    locale,
    pageType: 'about_us',
    errorTag: 'about_us',
    includeArticles: false,
    fields:[
      "*",
      {
        translations: [
          "*",
          {
            offers: ["*"],
          },
        ],
      },
    ],
  });

  return {
    props: {
      ...pageProps.props,
      whoWeAreHtml: parseEditorjsHtml(pageProps.props?.content?.who_we_are),
    },
  };
}

export default AboutPage;
